import { type CSSProperties, useSyncExternalStore } from 'react';
import hotToast, {
  Toaster as HotToaster,
  type ToasterProps,
} from 'react-hot-toast';
import { type ApiErrorSection, parseApiErrors } from '@core/utils/apiErrors';
import ErrorDialog from '../dialogs/ErrorDialog';

const listeners = new Set<() => void>();
let apiErrorsState = {
  open: false,
  sections: [] as ApiErrorSection[],
};

function notify() {
  listeners.forEach((listener) => listener());
}

const actionButtonStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  textDecoration: 'underline',
};

export const toast = Object.assign(hotToast, {
  action(message: string, options: { label: string; onClick: () => void }) {
    hotToast.success(
      <span>
        {message}{' '}
        <button
          type="button"
          onClick={() => {
            hotToast.dismiss();
            options.onClick();
          }}
          style={actionButtonStyle}
        >
          {options.label}
        </button>
      </span>
    );
  },
  apiError(message: string, errorOrErrors?: unknown) {
    const sections = parseApiErrors(errorOrErrors);

    if (!sections.length) {
      hotToast.error(message);
      return;
    }

    hotToast.error(
      <span>
        {message}{' '}
        <button
          type="button"
          onClick={() => {
            apiErrorsState = { open: true, sections };
            notify();
          }}
          style={actionButtonStyle}
        >
          See details
        </button>
      </span>
    );
  },
});

const Toaster = ({ position = 'top-center', ...props }: ToasterProps) => {
  const errorsState = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => apiErrorsState
  );

  return (
    <>
      <HotToaster position={position} {...props} />
      <ErrorDialog
        open={errorsState.open}
        sections={errorsState.sections}
        onClose={() => {
          if (!apiErrorsState.open) return;
          apiErrorsState = { open: false, sections: [] };
          notify();
        }}
      />
    </>
  );
};

export default Toaster;
